import { useState, useEffect, useRef } from "react";
import { Play, Pause, RotateCcw } from "lucide-react";

const PHASES = [
  { label: "Breathe In", duration: 4, color: "var(--ll-green)" },
  { label: "Hold", duration: 7, color: "var(--ll-accent)" },
  { label: "Breathe Out", duration: 8, color: "var(--ll-text-secondary)" },
];

const BreathingTool = () => {
  const [running, setRunning] = useState(false);
  const [cycle, setCycle] = useState(0);
  const [phase, setPhase] = useState(0);
  const [countdown, setCountdown] = useState(PHASES[0].duration);
  const [totalCycles, setTotalCycles] = useState(0);
  const intervalRef = useRef(null);

  // PHASES is a module-level constant, intervalRef is a ref — both stable
  useEffect(() => {
    if (!running) return;
    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setPhase((p) => {
            const next = (p + 1) % PHASES.length;
            if (next === 0) setTotalCycles((c) => c + 1);
            return next;
          });
          return PHASES[(phase + 1) % PHASES.length].duration;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [running, phase]);

  const reset = () => { setRunning(false); setPhase(0); setCountdown(PHASES[0].duration); setTotalCycles(0); };
  const currentPhase = PHASES[phase];
  const progress = 1 - countdown / currentPhase.duration;
  const circleSize = running ? (phase === 0 ? 220 : phase === 1 ? 220 : 140) : 180;

  return (
    <div data-testid="breathing-tool" className="rounded-2xl p-8" style={{ background: "white", border: "1px solid var(--ll-border)" }}>
      <h2 className="font-serif text-2xl font-medium mb-2" style={{ color: "var(--ll-text)" }}>Breathing Anchor</h2>
      <p className="text-sm mb-8" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
        The 4-7-8 technique activates your parasympathetic nervous system, reducing anxiety and promoting calm.
      </p>

      {/* Animated Circle */}
      <div className="flex flex-col items-center justify-center py-8">
        <div className="relative flex items-center justify-center" style={{ width: 240, height: 240 }}>
          <div className="absolute rounded-full transition-all duration-1000 ease-in-out" style={{
            width: circleSize, height: circleSize,
            background: running ? `${currentPhase.color}15` : "var(--ll-bg-secondary)",
            border: `3px solid ${running ? currentPhase.color : "var(--ll-border)"}`,
          }} />
          <div className="relative text-center z-10">
            <p className="font-serif text-3xl font-medium mb-1" style={{ color: running ? currentPhase.color : "var(--ll-text)" }}>
              {running ? countdown : "Ready"}
            </p>
            <p className="text-xs tracking-wider uppercase" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
              {running ? currentPhase.label : "Press play to begin"}
            </p>
          </div>
        </div>

        {/* Phase indicators */}
        {running && (
          <div className="flex gap-4 mt-6">
            {PHASES.map((p, i) => (
              <div key={p.label} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full transition-all" style={{ background: i === phase ? p.color : "var(--ll-border)" }} />
                <span className="text-[11px]" style={{ color: i === phase ? "var(--ll-text)" : "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
                  {p.label} ({p.duration}s)
                </span>
              </div>
            ))}
          </div>
        )}

        {totalCycles > 0 && (
          <p className="text-xs mt-4" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
            {totalCycles} cycle{totalCycles > 1 ? "s" : ""} completed
          </p>
        )}
      </div>

      {/* Controls */}
      <div className="flex justify-center gap-3">
        <button data-testid="breathing-toggle" onClick={() => setRunning(!running)}
          className="rounded-full px-8 py-3 text-sm font-medium flex items-center gap-2 transition-all hover:-translate-y-0.5"
          style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
          {running ? <><Pause size={14} /> Pause</> : <><Play size={14} /> {totalCycles > 0 ? "Resume" : "Start"}</>}
        </button>
        {totalCycles > 0 && (
          <button onClick={reset} className="rounded-full px-4 py-3 text-sm border transition-all hover:-translate-y-0.5"
            style={{ borderColor: "var(--ll-border)", color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
            <RotateCcw size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

export default BreathingTool;
